import {
  Box,
  Checkbox,
  type CheckboxProps,
  FormControlLabel,
  Grid,
  Radio,
  TextField,
  type TextFieldProps,
  Typography,
  useMediaQuery,
  Button,
  Dialog,
  DialogContent,
  type TypographyProps
} from '@mui/material'
import {
  filter,
  includes,
  isBoolean,
  isEmpty,
  isNil,
  map,
  replace,
  split,
  concat,
  find,
  pull
} from 'lodash/fp'
import React from 'react'
import {
  type InputProps,
  useInput,
  useTranslate,
  BooleanInput as RaBooleanInput,
  SelectInput as RaSelectInput
} from 'react-admin'
import {
  useFormState,
  useFormContext,
  type UseFormSetValue,
  type FieldValues,
  type UseFormGetValues
} from 'react-hook-form'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import ja from 'date-fns/locale/ja'
import {
  format,
  parse,
  isValid,
  parseISO,
  addMonths,
  eachDayOfInterval,
  lastDayOfMonth,
  subDays
} from 'date-fns/fp'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import log from 'loglevel'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { Calendar, DateObject, type Value } from 'react-multi-date-picker'
// import 'react-multi-date-picker/styles/layouts/mobile.css'

log.setLevel(log.levels.DEBUG)

export const ValidateError: React.FC<{
  errorMessage: string
}> = ({ errorMessage }) => {
  const translate = useTranslate()
  let message: string
  if (includes(':')(errorMessage)) {
    const items = split(':')(errorMessage)
    message = `${items[1] as string}${translate(items[0] as string)}`
  } else {
    message = translate(errorMessage)
  }
  return (
    <Typography variant="subtitle2" color="red" sx={{ marginLeft: '1em' }}>
      {message}
    </Typography>
  )
}

export const TextInput: React.FC<
  TextFieldProps & {
    source: string
    placeholder: string
    fields?: string
    validate?: any
    label?: string
    subsource?: string
    onChange?: (event: any) => void
  }
> = ({
  source,
  placeholder,
  validate,
  fields,
  label,
  subsource,
  inputMode,
  multiline,
  ...props
}) => {
  // const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const isDesktop = useMediaQuery('(min-width: 900px)')
  const { field, isRequired } = useInput({ source, validate, ...props })
  const translate = useTranslate()
  const name = replace(/\.\d/, '')(field.name)
  const labelString = isNil(label)
    ? translate(`${fields as string}.${name}`)
    : label
  const { errors } = useFormState()
  let error: any = errors
  if (!isNil(error)) {
    for (const field of split('.')(source)) {
      error = error[field]
      if (!isNil(error) && !isNil(subsource)) {
        error = error[subsource]
      }
      if (isNil(error)) break
    }
  }
  return (
    <Grid xs={12} marginBottom={'1em'}>
      <Typography
        variant="subtitle1"
        // sx={{ marginLeft: isDesktop ? '0.50em' : '0.25em' }}
      >
        {labelString}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em'
        }}>
        <TextField
          {...field}
          required={isRequired}
          fullWidth
          inputMode={inputMode}
          multiline={!isNil(multiline) && multiline}
          placeholder={placeholder}
          hidden={true}
          maxRows={!isNil(multiline) && multiline ? 10 : 1}
        />
      </Box>
      {!isNil(error) && <ValidateError errorMessage={error.message} />}
    </Grid>
  )
}
export const DateInput: React.FC<{
  source: string
  fields: string
  disableFuture?: boolean
  disablePast?: boolean
  alwaysOn?: boolean
  validate?: any
}> = ({
  source,
  disableFuture,
  disablePast,
  alwaysOn,
  validate,
  fields,
  ...props
}) => {
  const translate = useTranslate()
  const { field, isRequired } = useInput({ source, ...props })
  const { value } = field
  const [dateValue, setDateValue] = React.useState<Date | null>(
    !isNil(value) && !isEmpty(value) ? parseISO(value) : null
  )
  const { setValue } = useFormContext()
  const label = translate(`${fields}.${replace(/\.[0-9]+/g, '')(field.name)}`)
  React.useEffect(() => {
    setDateValue(!isNil(value) && !isEmpty(value) ? parseISO(value) : null)
  }, [value])
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={ja}
        localeText={{
          cancelButtonLabel: 'キャンセル',
          okButtonLabel: '選択'
        }}>
        <DatePicker
          value={
            isNil(dateValue) && !isNil(value) && !isEmpty(value)
              ? parseISO(value)
              : dateValue
          }
          format="yyyy-MM-dd"
          onChange={(newValue: Date | null): void => {
            if (isValid(newValue)) {
              const value = !isNil(newValue)
                ? format('yyyy-MM-dd')(newValue)
                : ''
              setDateValue(newValue)
              setValue(source, value, {
                shouldDirty: true,
                shouldValidate: true
              })
              field.onChange({ target: { value } })
            }
          }}
          slotProps={{
            field: {
              clearable: true,
              onClear: (): void => {
                setValue(source, undefined, {
                  shouldDirty: true,
                  shouldValidate: true
                })
                setDateValue(null)
                field.onChange({ target: { value: undefined } })
              },
              format: 'yyyy年M月d日'
            },
            textField: {
              required: isRequired,
              sx: {
                borderRadius: '10px',
                borderColor: '#c5c5c5'
              },
              variant: 'outlined',
              fullWidth: true,
              size: 'medium',
              inputProps: { style: { fontVariant: 'body1' } },
              InputLabelProps: { disabled: true },
              hiddenLabel: true
            },
            toolbar: {
              hidden: true
            }
          }}
          views={['year', 'month', 'day']}
          minDate={parse(new Date(), 'yyyy-MM-dd')('2023-01-01')}
          disableFuture={!isNil(disableFuture) ? disableFuture : false}
          disablePast={!isNil(disablePast) ? disablePast : false}
        />
      </LocalizationProvider>
    </>
  )
}
export const useScroll = (obj?: any): React.RefObject<HTMLDivElement> => {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  React.useLayoutEffect(() => {
    scrollRef?.current?.scrollIntoView({
      block: 'center'
    })
  }, [])
  React.useEffect(() => {
    if (!isNil(obj)) {
      scrollRef?.current?.scrollIntoView({
        block: 'center'
      })
    }
  }, [obj])
  return scrollRef
}
export const YesNoInput: React.FC<
  InputProps & { fields: string; labels: string[] }
> = ({ fields, labels, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { source } = props
  const translate = useTranslate()
  const { field } = useInput(props)
  const { value } = field
  const [radioValue, setRadioValue] = React.useState<boolean | undefined>(
    !isBoolean(value) ? undefined : value
  )
  const { setValue } = useFormContext()
  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value === 'true'
    const { currentTarget } = event
    setRadioValue(value)
    setValue(source, value)
    field.onChange({
      currentTarget,
      target: { value }
    })
  }
  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          <Grid
            item
            xs={isDesktop ? 3 : 12}
            sx={{
              display: 'flex',
              justifyContent: isDesktop ? 'right' : 'center',
              alignItems: 'center'
            }}>
            <Typography variant="body1" sx={{ mr: '0.5em' }}>
              {translate(`${fields}.${replace(/\.\d/, '')(source)}`)}
            </Typography>
          </Grid>
          <Grid item xs={isDesktop ? 1 : 6}>
            <FormControlLabel
              control={
                <Radio
                  checked={
                    (!isNil(radioValue) && radioValue) ||
                    (isBoolean(value) && value)
                  }
                  checkedIcon={<CheckBoxIcon />}
                  icon={<CheckBoxOutlineBlankIcon />}
                  onChange={onChange}
                  value="true"
                  name={source}
                />
              }
              label={labels[0]}
            />
          </Grid>
          <Grid item xs={isDesktop ? 2 : 6}>
            <FormControlLabel
              control={
                <Radio
                  checked={
                    (!isNil(radioValue) && !radioValue) ||
                    (isBoolean(value) && !value)
                  }
                  checkedIcon={<CheckBoxIcon />}
                  icon={<CheckBoxOutlineBlankIcon />}
                  onChange={onChange}
                  value="false"
                  name={source}
                />
              }
              label={labels[1]}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
export const BooleanInput: React.FC<InputProps & { fields: string }> = ({
  fields,
  ...props
}) => {
  const { source } = props
  const translate = useTranslate()
  return (
    <RaBooleanInput
      source={source}
      label={translate(`${fields}.${replace(/\.\d/, '')(source)}`)}
    />
  )
}
export const RadioInput: React.FC<
  InputProps & {
    fields: string
    labels1: string[]
    labels2?: string[]
    values: string[]
    descriptions1?: string[]
    descriptions2?: string[]
  }
> = ({
  fields,
  labels1,
  labels2,
  values,
  descriptions1,
  descriptions2,
  ...props
}) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { source } = props
  const { field } = useInput(props)
  const { value } = field
  const [radioValue, setRadioValue] = React.useState<string | undefined>(
    isEmpty(value) ? undefined : value
  )
  const { setValue } = useFormContext()
  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value
    const { currentTarget } = event
    setRadioValue(value)
    setValue(source, value)
    field.onChange({
      currentTarget,
      target: { value }
    })
  }
  return (
    <Grid
      container
      xs={isDesktop ? 6 : 11}
      sx={{
        display: 'grid',
        justifyContent: 'normal',
        alignItems: 'center'
      }}>
      {
        // @ts-expect-error ts(2339)
        map.convert({ cap: false })((label: string, index: number) => {
          return (
            <Grid
              item
              marginY={2}
              sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Radio
                    checked={
                      !isEmpty(radioValue) && radioValue === values[index]
                    }
                    checkedIcon={<CheckBoxIcon />}
                    icon={<CheckBoxOutlineBlankIcon />}
                    onChange={onChange}
                    value={values[index]}
                    sx={{}}
                  />
                }
                label=""
                sx={{
                  whiteSpace: 'pre-wrap',
                  mb: '-0.5em'
                }}
              />
              <Grid>
                <Grid container display="flex">
                  <Typography
                    variant="body1"
                    sx={{ 'white-space': 'pre-wrap' }}>
                    {labels1[index]}
                  </Typography>
                  <Grid
                    item
                    alignContent="end"
                    sx={{
                      ml: '-1.5em'
                    }}>
                    {!isNil(labels2) ? (
                      <Typography
                        variant="body2"
                        sx={{
                          'white-space': 'pre-wrap',
                          fontStyle: 'italic'
                        }}>
                        {labels2[index]}
                      </Typography>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Grid>
                {!isNil(descriptions1) ? (
                  <Typography
                    variant="body1"
                    sx={{
                      mt: '0.5em',
                      mr: '0.5em',
                      'white-space': 'pre-wrap',
                      fontSize: 15
                    }}>
                    {descriptions1[index]}
                  </Typography>
                ) : (
                  <></>
                )}
                {!isNil(descriptions2) ? (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: '0.5em',
                      mr: '0.5em',
                      'white-space': 'pre-wrap'
                    }}>
                    {descriptions2[index]}
                  </Typography>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          )
        })(labels1)
      }
    </Grid>
  )
}
export const CheckInput: React.FC<
  InputProps & {
    fields: string
    title?: string
    labels1: string[]
    labels2?: string[]
    values: string[]
    descriptions1?: string[]
    descriptions2?: string[]
    subsource?: string
  }
> = ({
  fields,
  title,
  labels1,
  labels2,
  values,
  descriptions1,
  descriptions2,
  subsource,
  ...props
}) => {
  // const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const isDesktop = useMediaQuery('(min-width: 900px)')
  const { source } = props
  const { getValues, setValue } = useFormContext()
  const { errors } = useFormState()
  let error: any = errors
  if (!isNil(error)) {
    for (const field of split('.')(source)) {
      error = error[field]
      if (!isNil(error) && !isNil(subsource)) {
        error = error[subsource]
      }
      if (isNil(error)) break
    }
  }
  return (
    <>
      <Grid
        container
        xs={isDesktop ? 6 : 11}
        sx={{
          display: 'grid',
          justifyContent: 'normal',
          alignItems: 'center'
        }}>
        {!isNil(title) && (
          <Box sx={{ paddingBottom: '1em' }}>
            <Typography variant="body1" sx={{ 'white-space': 'pre-wrap' }}>
              {title}
            </Typography>
          </Box>
        )}
        {
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((_label: string, index: number) => {
            return (
              <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckboxItem
                  color="primary"
                  source={source}
                  checkedIcon={<CheckBoxIcon color="primary" />}
                  icon={<CheckBoxOutlineBlankIcon color="primary" />}
                  value={values[index]}
                  setValue={setValue}
                  getValues={getValues}
                />
                <Grid>
                  <Grid container display="flex">
                    <Typography
                      variant="body1"
                      sx={{ 'white-space': 'pre-wrap' }}>
                      {labels1[index]}
                    </Typography>
                    <Grid
                      alignContent="end"
                      sx={{
                        ml: '-1.5em'
                      }}>
                      {!isNil(labels2) ? (
                        <Typography
                          variant="body2"
                          sx={{
                            'white-space': 'pre-wrap',
                            fontStyle: 'italic'
                          }}>
                          {labels2[index]}
                        </Typography>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                  {!isNil(descriptions1) ? (
                    <Typography
                      variant="body1"
                      sx={{
                        mt: '0.5em',
                        mr: '0.5em',
                        'white-space': 'pre-wrap',
                        fontSize: 15
                      }}>
                      {descriptions1[index]}
                    </Typography>
                  ) : (
                    <></>
                  )}
                  {!isNil(descriptions2) ? (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: '0.5em',
                        mr: '0.5em',
                        'white-space': 'pre-wrap'
                      }}>
                      {descriptions2[index]}
                    </Typography>
                  ) : (
                    <></>
                  )}
                </Grid>
              </Grid>
            )
          })(labels1)
        }
      </Grid>
      {!isNil(error) && <ValidateError errorMessage={error.message} />}
    </>
  )
}
const CheckboxItem: React.FC<
  CheckboxProps & {
    source: string
    getValues: UseFormGetValues<FieldValues>
    setValue: UseFormSetValue<FieldValues>
  }
> = ({ getValues, setValue, ...props }) => {
  const { source } = props
  const { field } = useInput(props)
  const [isChecked, setIsChecked] = React.useState<boolean>(false)
  const { value: propsValue } = props
  const values = getValues(source) ?? []
  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value
    const { currentTarget } = event
    field.onChange({
      currentTarget,
      target: { newValue }
    })
    if (isChecked) {
      setIsChecked(false)
      setValue(
        source,
        filter((oldValue: string) => oldValue !== newValue)(values),
        { shouldValidate: true, shouldDirty: true }
      )
    } else {
      setIsChecked(true)
      setValue(source, [...values, newValue], {
        shouldValidate: true,
        shouldDirty: true
      })
    }
  }
  React.useEffect(() => {
    if (includes(propsValue)(values)) {
      setIsChecked(true)
    }
  }, [propsValue, values])
  return (
    <Checkbox
      checked={isChecked}
      checkedIcon={<CheckBoxIcon />}
      icon={<CheckBoxOutlineBlankIcon />}
      onChange={onChange}
      value={propsValue}
      sx={{ '&.MuiCheckbox-root.Mui-checked': { color: '#f57800' } }}
    />
  )
}
export const ScheduleInput: React.FC<{
  source: string
  onSet: () => void
  onCancel: () => void
  current: string[] | undefined
}> = ({ source, current, onSet, onCancel, ...props }) => {
  const translate = useTranslate()
  // const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const isDesktop = useMediaQuery('(min-width: 900px)')
  const today = new Date()
  const start = subDays(1)(today)
  const { setValue, getValues } = useFormContext()
  const { ...data } = getValues()
  const schedules = data[source]
  const [values, setValues] = React.useState<Value[][]>(
    !isNil(schedules)
      ? map((item: string) => [item, item])(schedules)
      : [[today]]
  )
  const [currentDate, setCurrentDate] = React.useState<DateObject>(
    new DateObject(today)
  )
  const dateString = (item: Value | undefined): string =>
    item instanceof Date || item instanceof DateObject
      ? format('yyyy-MM-dd')(item instanceof Date ? item : item.toDate())
      : isNil(item)
      ? 'undefined'
      : item.toString()
  const dateArrayString = (
    items: Value[][] | Value[] | undefined
  ): string[][] =>
    isNil(items)
      ? [['undefined']]
      : map((item: Value[] | Value) =>
          map((subItem: Value) => dateString(subItem))(
            item instanceof Array ? item : [item]
          )
        )(items)
  const jaJp = {
    name: 'ja_jp',
    months: [
      ['1月', '1月'],
      ['2月', '2月'],
      ['3月', '3月'],
      ['4月', '4月'],
      ['5月', '5月'],
      ['6月', '6月'],
      ['7月', '7月'],
      ['8月', '8月'],
      ['9月', '9月'],
      ['10月', '10月'],
      ['11月', '11月'],
      ['12月', '21月']
    ],
    weekDays: [
      ['土曜日', '土'],
      ['日曜日', '日'],
      ['月曜日', '月'],
      ['火曜日', '火'],
      ['水曜日', '水'],
      ['木曜日', '木'],
      ['金曜日', '金']
    ],
    digits: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
    meridiems: [
      ['午前', '午前'],
      ['午後', '午後']
    ]
  }
  const { field } = useInput({ source, ...props })
  const setFormValue = (values: DateObject[][]): void => {
    setValues(values)
    const value = map((item: DateObject[]) => dateString(item[0]))(values)
    // setValue(source, value)
    field.onChange(value)
  }
  const onChange = (newValues: DateObject[][]): void => {
    const valuesString = dateArrayString(values)
    if (
      valuesString.toString() ===
      [dateString(today), dateString(today)].toString()
    ) {
      const value = find(
        (item: Value) =>
          (item instanceof Array
            ? dateArrayString(item)
            : dateString(item)
          ).toString() !== valuesString.toString()
      )(newValues)
      if (
        !isNil(value) &&
        value instanceof Array &&
        value[0] instanceof DateObject
      ) {
        const adjust = map((item: Date) => [
          new DateObject(item),
          new DateObject(item)
        ])(eachDayOfInterval({ start: today, end: value[0].toDate() }))
        // setValues(adjust)
        setFormValue(adjust)
        return
      }
    }
    let adjust: DateObject[][] = []
    for (const value of newValues) {
      if (
        value instanceof Array &&
        value.length > 1 &&
        value[0] instanceof DateObject &&
        value[1] instanceof DateObject
      ) {
        if (dateString(value[0]) !== dateString(value[1])) {
          if (dateString(value[0]) === dateString(today)) {
            const flat = map((item: Date) => [
              new DateObject(item),
              new DateObject(item)
            ])(
              eachDayOfInterval({
                start: value[0].toDate(),
                end: value[1].toDate()
              })
            )
            adjust = concat(flat)(adjust)
          } else {
            adjust.push([value[0], value[0]])
            adjust.push([value[1], value[1]])
          }
        } else {
          adjust.push(value)
        }
      } else {
        if (!isNil(value) && !isNil(value[0])) {
          adjust.push([value[0], value[0]])
        }
      }
    }
    setFormValue(adjust)
  }
  const onFocusedDateChange = (
    _focused: DateObject,
    clicked: DateObject
  ): void => {
    setCurrentDate(clicked)
    if (
      dateString(clicked) === dateString(start) &&
      dateArrayString(values).toString() !== [dateString(start)].toString()
    ) {
      // setValues(values)
      setFormValue(values as DateObject[][])
      return
    }
    const deleted = find(
      (item: DateObject[] | Date) =>
        dateString(clicked) ===
        dateString(item instanceof Array ? item[0] : item)
    )(values)
    if (!isNil(deleted) && !isEmpty(deleted)) {
      setFormValue(pull(deleted)(values as DateObject[][]) as DateObject[][])
    }
  }
  const ClearButton: React.FC = () => {
    const onClick = (): void => {
      setValues([[today]])
      setCurrentDate(new DateObject(today))
      setValue(source, [])
      field.onChange([])
    }
    return (
      <Button
        variant="contained"
        onClick={onClick}
        fullWidth={!isDesktop}
        sx={{
          borderRadius: '20px'
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate('scheduleInput.clear')}
        </Typography>
      </Button>
    )
  }
  const SetButton: React.FC = () => {
    const onClick = (): void => {
      const currentValues = getValues()
      if (!isEmpty(currentValues[source])) {
        const valuesString = dateArrayString(values)
        const value = map((item: string[]) => item[0])(valuesString)
        setValue(source, value)
      }
      onSet()
    }
    return (
      <Button
        variant="contained"
        onClick={onClick}
        fullWidth={!isDesktop}
        sx={{
          borderRadius: '20px'
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate('scheduleInput.set')}
        </Typography>
      </Button>
    )
  }
  const CancelButton: React.FC = () => {
    const onClick = (): void => {
      setValue(source, current)
      field.onChange(current)
      onCancel()
    }
    return (
      <Button
        variant="outlined"
        onClick={onClick}
        fullWidth={!isDesktop}
        sx={{
          borderRadius: '20px'
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate('scheduleInput.cancel')}
        </Typography>
      </Button>
    )
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box width="100%">
        <Grid
          container
          justifyContent="center"
          spacing={2}
          sx={{
            marginTop: '0.1em'
          }}>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1em',
              marginTop: '1em'
            }}>
            <Calendar
              multiple
              range
              value={values}
              currentDate={currentDate}
              minDate={today}
              maxDate={lastDayOfMonth(addMonths(1)(today))}
              onChange={onChange}
              onFocusedDateChange={onFocusedDateChange}
              className="rmdp-mobile"
              // style={
              //   isDesktop
              //     ? {
              //         transform: 'scale(1.5, 1.5)',
              //         marginTop: '3em',
              //         marginBottom: '3em',
              //         fontWeight: 'bold'
              //       }
              //     : {
              //         transform: 'scale(1.2, 1.2)',
              //         marginTop: '3em',
              //         marginBottom: '1em',
              //         fontWeight: 'bold'
              //       }
              // }
              mapDays={() => {
                const props = {
                  style: {
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }
                }
                return props
              }}
              locale={jaJp}
              monthYearSeparator=" "
              formatMonth={(_month: string, year: string) => {
                return `${year}年`
              }}
              formatYear={(_year: string, month: string) => {
                return month
              }}
              disableYearPicker
              disableMonthPicker
            />
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              marginTop: '1em',
              marginLeft: '0.5em'
            }}>
            <Grid
              item
              xs={isDesktop ? 2 : 12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: isDesktop ? '2.5em' : '0em'
              }}>
              {isDesktop ? <ClearButton /> : <SetButton />}
            </Grid>
            <Grid
              item
              xs={isDesktop ? 3 : 12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: isDesktop ? '2.5em' : '0em'
              }}>
              <CancelButton />
            </Grid>
            <Grid
              item
              xs={isDesktop ? 2 : 12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: isDesktop ? '2.5em' : '0em'
              }}>
              {isDesktop ? <SetButton /> : <ClearButton />}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
export const ScheduleButton: React.FC<{
  source: string
  label: string
}> = ({ source, label }) => {
  const translate = useTranslate()
  const [open, setOpen] = React.useState<boolean>(false)
  const { getValues } = useFormContext()
  const current = getValues()
  const onClick = (): void => {
    setOpen(true)
  }
  const handleClose = (): void => {
    setOpen(false)
  }
  const { errors } = useFormState()
  let error: any = errors
  if (!isNil(error)) {
    for (const field of split('.')(source)) {
      error = error[field]
      if (isNil(error)) break
    }
  }
  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={onClick}
        sx={{
          borderRadius: '20px',
          backgroundColor: '#f57800'
        }}
        color="primary">
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate(label)}
        </Typography>
      </Button>
      <Dialog onClose={handleClose} open={open} maxWidth="md" fullWidth>
        <DialogContent>
          <ScheduleInput
            source={source}
            current={current[source]}
            onSet={handleClose}
            onCancel={handleClose}
          />
        </DialogContent>
      </Dialog>
      {!isNil(error) && <ValidateError errorMessage={error.message} />}
    </>
  )
}
export const TextShow: React.FC<TypographyProps & { label?: string }> = ({
  label,
  children
  // ...props
}) => {
  // const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const isDesktop = useMediaQuery('(min-width: 900px)')
  return (
    <Box>
      {!isNil(label) && (
        <Typography
          variant="subtitle1"
          // sx={{ marginLeft: isDesktop ? '0.50em' : '0.25em' }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          // height: '2em',
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'center'
          // marginTop: isDesktop ? '0.1em' : '0.25em',
          // marginLeft: isDesktop ? '0.50em' : '0.25em',
          // marginBottom: '2em',
          // borderRadius: '10px',
          // borderColor: '#c5c5c5'
        }}>
        <Typography
          variant="body1"
          whiteSpace={'pre-line'}
          sx={{ marginTop: isDesktop ? '1em' : '0.25em' }}>
          {children}
        </Typography>
      </Box>
    </Box>
  )
}
export const SelectInput: React.FC<
  InputProps & { fields: string; labels: string[]; values: string[] }
> = ({ fields, labels, values, ...props }) => {
  const isDesktop = useMediaQuery('(min-width: 900px)')
  const { source } = props
  const translate = useTranslate()
  return (
    <Grid xs={3}>
      <Typography variant="subtitle1">
        {translate(`${fields}.${replace(/\.\d/, '')(source)}`)}
      </Typography>
      <RaSelectInput
        {...props}
        label={false}
        choices={
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((label: string, index: number) => {
            return { id: values[index], name: label }
          })(labels)
        }
        fullWidth
        sx={{
          paddingLeft: isDesktop ? '0.5em' : '0.25em',
          paddingRight: isDesktop ? '0.5em' : '0.25em'
        }}
      />
    </Grid>
  )
}
